import { DateFormats, formatDate } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { TextAreaFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { transitionTreatment } from 'api/treatmentAPI';
import { handleError } from 'services/Error';
import { useGetPayersQuery } from 'store/api/referenceApi';

const ReasonOptions = [
    'Contractual',
    'Drug access change',
    'Out of network pharmacy',
    'State capability change',
];

export const BulkTransition = () => {
    const { data, isFetching } = useGetPayersQuery();
    const { payerList } = data ?? {};

    return (
        <FormHeaderAndContent
            mainContent={
                <Formik
                    initialValues={{}}
                    validationSchema={Yup.object({
                        payerId: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        treatmentIds: Yup.string()
                            .required(validationStrings.requiredField.errorMessage)
                            .matches(/^.+$/gm, 'IDs formatted incorrectly.'),
                        transitionDate: DateInputFormik.validator().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        ...ReasonInput.validationSchema,
                    })}
                    onSubmit={async (values, { resetForm }) => {
                        try {
                            const errors = (
                                await Promise.all(
                                    values.treatmentIds?.match(/^.+$/gm).map((treatmentId) =>
                                        (async () => {
                                            try {
                                                await transitionTreatment({
                                                    payerId: values.payerId,
                                                    treatmentId: treatmentId.trim(),
                                                    transitionDate: formatDate(
                                                        values?.transitionDate,
                                                        DateFormats.HYPHEN_DATE,
                                                    ),
                                                    ...ReasonInput.submitValues(values),
                                                });
                                                return undefined;
                                            } catch {
                                                return treatmentId;
                                            }
                                        })(),
                                    ),
                                )
                            ).filter((err) => err);

                            if (errors.length > 0) {
                                ErrorToast({
                                    message: (
                                        <div>
                                            <div>
                                                The following treatments could not be transitioned:
                                            </div>
                                            {errors.map((id) => (
                                                <div key={id}>{id}</div>
                                            ))}
                                        </div>
                                    ),
                                    timeout: 0,
                                });
                            } else {
                                SuccessToast({ message: 'Referrals transitioned successfully.' });
                                resetForm();
                            }
                        } catch (err) {
                            handleError(
                                'There was an error attempting this action, please try again.',
                                err,
                            );
                        }
                    }}
                >
                    {(formik) => (
                        <FormContainer
                            formTitle="Bulk Transition"
                            primaryButtonLabel="Transition Referrals"
                            onFormSubmit={formik.handleSubmit}
                            onCancelClick={formik.handleReset}
                            isSubmitting={formik.isSubmitting || !(formik.isValid && formik.dirty)}
                            formLeftContent={
                                <>
                                    <FullscreenLoading
                                        isShowing={formik.isSubmitting}
                                        loadingText="Transitioning referrals..."
                                    />
                                    <div className="margin__bottom--medium">
                                        <InlineMessage
                                            intent={InlineMessage.Intent.INFO}
                                            wrapText
                                            label="This action will send the active referral for a treatment back through our platform. e.g. routing, claims check, etc."
                                        />
                                    </div>
                                    <SuggestSingleSelectFormik
                                        id="payerId"
                                        label="Health Plan"
                                        required
                                        isLoading={isFetching}
                                        values={payerList}
                                        selectedItem={payerList?.find(
                                            ({ value }) => value === formik.values.payerId,
                                        )}
                                        onChange={(newValue) =>
                                            formik.setFieldValue('payerId', newValue?.value)
                                        }
                                    />
                                    <TextAreaFormik
                                        label="Treatment IDs"
                                        id="treatmentIds"
                                        helperText="One ID per line."
                                        required
                                    />
                                    <DateInputFormik
                                        label="Date To Transition"
                                        id="transitionDate"
                                        required
                                    />
                                    <ReasonInput reasons={ReasonOptions} />
                                </>
                            }
                        />
                    )}
                </Formik>
            }
        />
    );
};
